import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { copyToClipboard } from '../utils/clipboard';
import { buildReferralLink } from '../utils/referral';
import { formatDateTime, formatDateOnly } from '../utils/timezone';
import ExchangeRates from '../components/ExchangeRates';
import PlacementWelcomeModal from '../components/PlacementWelcomeModal';
import RepurchaseCreditModal from '../components/RepurchaseCreditModal';
import RnrConversionModal from '../components/RnrConversionModal';
import SendPointsModal from '../components/SendPointsModal';
import TopUpModal from '../components/TopUpModal';

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const { t, i18n } = useTranslation('app');
  const navigate = useNavigate();
  const [stats] = useState({
    downlineCount: 0,
    activePackages: 0,
    currentRank: 'Member',
  });
  const [creditBalance, setCreditBalance] = useState(0);
  const [cashBalance, setCashBalance] = useState(0);
  const [productBalance, setProductBalance] = useState(0);
  const [rnrBalance, setRnrBalance] = useState(0);
  const [showRnrConversionModal, setShowRnrConversionModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [referralCommissions, setReferralCommissions] = useState([]);
  const [referralInfo, setReferralInfo] = useState(null);
  const [packageStatus, setPackageStatus] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showRepurchaseModal, setShowRepurchaseModal] = useState(false);
  const [showSendPointsModal, setShowSendPointsModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [processingAction, setProcessingAction] = useState(false);
  const [approvedMembers, setApprovedMembers] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [exchangeRatesLoading, setExchangeRatesLoading] = useState(false);
  const [repurchaseRate, setRepurchaseRate] = useState(null);
  const [repurchaseRateInput, setRepurchaseRateInput] = useState('');
  const [repurchaseRateNotes, setRepurchaseRateNotes] = useState('');
  const [topupUserId, setTopupUserId] = useState('');
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNotes, setTopupNotes] = useState('');
  const [exchangeRateHistory, setExchangeRateHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [wellisId, setWellisId] = useState(null);
  const [topupBanner, setTopupBanner] = useState(null);

  const loadBalances = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-balance`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch(() => ({ ok: false }));

      if (res.ok) {
        try {
          const creditData = await res.json();
          setCreditBalance(creditData.credit_point_balance || 0);
          setCashBalance(creditData.cash_point_balance || 0);
          setProductBalance(creditData.product_point_balance || 0);
          setRnrBalance(creditData.rnr_point_balance || 0);
        } catch (e) {
          console.error('[Dashboard] Failed to parse credit-balance:', e.message);
        }
      }
    } catch (err) {
      console.error('[Dashboard] loadBalances error:', err);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-transactions?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch(() => ({ ok: false }));

      if (res.ok) {
        try {
          const data = await res.json();
          setTransactions(data.transactions || []);
        } catch (e) {
          console.error('[Dashboard] Failed to parse credit-transactions:', e.message);
        }
      }
    } catch (err) {
      console.error('[Dashboard] loadTransactions error:', err);
    }
  }, []);

  const loadReferralCommissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/direct-referral-commissions?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).catch(() => ({ ok: false }));

      if (res.ok) {
        try {
          const data = await res.json();
          setReferralCommissions(data.commissions || []);
        } catch (e) {
          console.error('[Dashboard] Failed to parse direct-referral-commissions:', e.message);
        }
      }
    } catch (err) {
      console.error('[Dashboard] loadReferralCommissions error:', err);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      const fetchPromises = [
        loadBalances(),
        loadTransactions(),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/referral-info`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }).catch(() => ({ ok: false })),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/package-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }).catch(() => ({ ok: false })),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/profile-data`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }).catch(() => ({ ok: false })),
        loadReferralCommissions(),
      ];

      const results = await Promise.all(fetchPromises);
      const referralRes = results[2];
      const packageRes = results[3];
      const profileRes = results[4];

      if (referralRes.ok) {
        try {
          const referralData = await referralRes.json();
          console.log('[Dashboard] referralInfo:', referralData);
          setReferralInfo(referralData);
        } catch (e) {
          console.error('[Dashboard] Failed to parse referral-info:', e.message);
        }
      }

      if (packageRes.ok) {
        try {
          const packageData = await packageRes.json();
          console.log('[Dashboard] packageStatus:', packageData);
          setPackageStatus(packageData);
        } catch (e) {
          console.error('[Dashboard] Failed to parse package-status:', e.message);
        }
      }

      if (profileRes.ok) {
        try {
          const profileData = await profileRes.json();
          if (profileData.affiliate_code) {
            setWellisId(profileData.affiliate_code);
          }
        } catch (e) {
          console.error('[Dashboard] Failed to parse profile-data:', e.message);
        }
      }

    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    }
  }, [loadBalances, loadTransactions, loadReferralCommissions]);

  useEffect(() => {
    const hidePlacementWelcome = localStorage.getItem('hidePlacementWelcome');
    if (user?.status === 'approved' && !hidePlacementWelcome) {
      setShowWelcomeModal(true);
    }
  }, [user?.status]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [authLoading, user, navigate, fetchDashboardData]);

  // Returning here from Stripe Checkout after a Credit Points top-up
  // (credit-topup-create-stripe.js's success_url/cancel_url both point
  // back to /dashboard, since that's where the "Top up Credit Points"
  // button lives). Show a brief confirmation, refresh the balance so a
  // successful top-up is visible immediately, then strip the query
  // params so a page refresh doesn't re-show the banner.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const topupStatus = params.get('topup');
    if (!topupStatus) return;

    if (topupStatus === 'success') {
      setTopupBanner({ type: 'success', key: 'topupSuccess' });
      loadBalances();
    } else if (topupStatus === 'cancelled') {
      setTopupBanner({ type: 'cancelled', key: 'topupCancelled' });
    }

    params.delete('topup');
    params.delete('topup_id');
    const query = params.toString();
    navigate(`/dashboard${query ? `?${query}` : ''}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExchangeRateHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/exchange-rates/history`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setExchangeRateHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch exchange rate history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchRepurchaseRateData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-repurchase-rate`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const rateData = await response.json();
        setRepurchaseRate(rateData.rate);
      } else {
        console.error('Failed to fetch repurchase rate:', response.status);
      }
    } catch (err) {
      console.error('Failed to fetch credit repurchase rate:', err);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'superadmin') return;

    const fetchApprovedMembers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/approved`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setApprovedMembers(data.users || []);
        }
      } catch (err) {
        console.error('Failed to fetch approved members:', err);
      }
    };

    fetchApprovedMembers();
    fetchExchangeRateHistory();
    fetchRepurchaseRateData();
  }, [user?.role, fetchExchangeRateHistory, fetchRepurchaseRateData]);

  const handleCopyReferralLink = async () => {
    if (!referralInfo?.affiliate_code) return;
    const referralLink = buildReferralLink(referralInfo.affiliate_code);
    const success = await copyToClipboard(referralLink);
    if (success) {
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    }
  };

  const getMembershipStatus = () => {
    if (packageStatus?.verified_internal) {
      return i18n.language === 'zh' ? `${t('dashboard.verified')}（内部）` : `${t('dashboard.verified')} (Internal)`;
    }
    if (packageStatus?.verified) {
      const packageName = i18n.language === 'zh'
        ? packageStatus.package?.name_cn
        : packageStatus.package?.name_en;
      return `${t('dashboard.verified')} - ${packageName || 'Member'}`;
    }
    return t('dashboard.notVerified');
  };

  const handleTopupCredits = async (e) => {
    e.preventDefault();
    if (!topupUserId || !topupAmount || !topupNotes.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setProcessingAction(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/credit-topup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: topupUserId,
          amount: parseFloat(topupAmount),
          notes: topupNotes.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to process credit topup');
      }

      alert('Credit topup processed successfully');
      setTopupUserId('');
      setTopupAmount('');
      setTopupNotes('');
    } catch (err) {
      alert(err.message);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleUpdateRepurchaseRate = async () => {
    if (!repurchaseRateInput || parseFloat(repurchaseRateInput) <= 0) {
      alert('Invalid rate');
      return;
    }

    setExchangeRatesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/credit-repurchase-rate/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          new_rate: parseFloat(repurchaseRateInput),
          notes: repurchaseRateNotes.trim() || null,
        }),
      });

      const data = await response.json();
      console.log('[Dashboard] Credit repurchase rate update response:', { status: response.status, data });

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update credit repurchase rate');
      }

      alert('Credit Repurchase Rate updated successfully');
      setRepurchaseRateInput('');
      setRepurchaseRateNotes('');

      // Wait for database transaction to commit before refetching
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refetch to confirm update
      console.log('[Dashboard] Refetching credit repurchase rate');
      await fetchRepurchaseRateData();
    } catch (err) {
      console.error('[Dashboard] Credit repurchase rate update error:', err);
      alert(err.message);
    } finally {
      setExchangeRatesLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-margin-mobile pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-container-max mx-auto">
        {/* Header */}
        <div className="mb-8 border-b pb-6" style={{ borderColor: '#c9a961' }}>
          <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>{t('dashboard.title')}</h1>
          <p className="text-body-md" style={{ color: '#b8a68f' }}>
            Welcome back{wellisId && (
              <span style={{ color: '#c9a961', fontFamily: 'monospace' }}>
                , {wellisId}
              </span>
            )}
          </p>
        </div>

        {topupBanner && (
          <div
            className="border rounded-lg p-4 mb-6 flex items-center justify-between gap-3"
            style={topupBanner.type === 'success'
              ? { backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#c9a961' }
              : { backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#b8a68f' }}
          >
            <span>
              {topupBanner.type === 'success'
                ? t('dashboard.topupSuccess', 'Payment received — your Credit Points have been topped up.')
                : t('dashboard.topupCancelled', 'Top-up cancelled — no payment was made.')}
            </span>
            <button
              type="button"
              onClick={() => setTopupBanner(null)}
              className="text-body-sm font-medium opacity-70 hover:opacity-100"
              style={{ color: 'inherit' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Tabs - Only show Credits & Rates for superadmin */}
        {user?.role === 'superadmin' && (
          <div className="flex gap-4 mb-6 border-b overflow-x-auto" style={{ borderColor: '#c9a961' }}>
            <button
              onClick={() => setSelectedTab('overview')}
              className="px-6 py-3 font-medium transition-colors whitespace-nowrap"
              style={{
                color: selectedTab === 'overview' ? '#c9a961' : '#b8a68f',
                borderBottom: selectedTab === 'overview' ? '2px solid #c9a961' : 'none',
              }}
            >
              Overview
            </button>
            <button
              onClick={() => setSelectedTab('credits_rates')}
              className="px-6 py-3 font-medium transition-colors whitespace-nowrap"
              style={{
                color: selectedTab === 'credits_rates' ? '#c9a961' : '#b8a68f',
                borderBottom: selectedTab === 'credits_rates' ? '2px solid #c9a961' : 'none',
              }}
            >
              Credits & Rates
            </button>
          </div>
        )}

        {/* Overview Tab Content */}
        {selectedTab === 'overview' && (
          <>
        {/* Membership Status */}
        <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{t('dashboard.membershipStatus')}</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span style={{ color: '#b8a68f' }}>Status</span>
              <div className="flex items-center gap-3">
                <span style={{ fontSize: '20px' }}>
                  {packageStatus?.verified ? '✓' : packageStatus?.pending_order ? '⏳' : '○'}
                </span>
                <span className="font-medium" style={{ color: '#c9a961' }}>
                  {getMembershipStatus()}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span style={{ color: '#b8a68f' }}>Ranking</span>
              <span className="text-headline-md font-bold" style={{ color: '#c9a961' }}>
                {packageStatus?.ranking ? '⭐'.repeat(packageStatus.ranking) : '—'}
              </span>
            </div>

            {packageStatus?.verified_at && (
              <div className="flex items-center justify-between">
                <span style={{ color: '#b8a68f' }}>Last Purchase</span>
                <span className="font-medium" style={{ color: '#c9a961' }}>
                  {formatDateOnly(packageStatus.verified_at)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="border rounded-lg p-4 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <p className="text-body-sm mb-2" style={{ color: '#b8a68f' }}>{t('dashboard.downlineCount')}</p>
            <p className="text-headline-md font-bold" style={{ color: '#c9a961' }}>{stats.downlineCount}</p>
          </div>
          <div className="border rounded-lg p-4 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <p className="text-body-sm mb-2" style={{ color: '#b8a68f' }}>{t('dashboard.activePackages')}</p>
            <p className="text-headline-md font-bold" style={{ color: '#c9a961' }}>{stats.activePackages}</p>
          </div>
        </div>

        {/* My Points & Referral Card */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* My Points Card */}
          <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{t('dashboard.myPoints')}</h2>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center">
                <span style={{ color: '#b8a68f' }}>{t('dashboard.creditPoints')}</span>
                <span className="text-headline-md font-bold" style={{ color: '#c9a961' }}>{creditBalance.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ color: '#b8a68f' }}>{t('dashboard.cashPoints')}</span>
                <span className="text-headline-md font-bold" style={{ color: '#c9a961' }}>{cashBalance.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ color: '#b8a68f' }}>{t('dashboard.productPoints')}</span>
                <span className="text-headline-md font-bold" style={{ color: '#c9a961' }}>{productBalance.toFixed(2)}</span>
              </div>
              {user?.role === 'sales-team' && (
                <div className="flex justify-between items-center">
                  <span style={{ color: '#b8a68f' }}>{t('dashboard.rnrPoints')}</span>
                  <span className="text-headline-md font-bold" style={{ color: '#c9a961' }}>{rnrBalance.toFixed(2)}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              {user?.role !== 'superadmin' && (
                <button
                  onClick={() => setShowTopUpModal(true)}
                  className="w-full px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
                  style={{ backgroundColor: '#c9a961' }}
                >
                  {t('dashboard.topUpCreditPoints')}
                </button>
              )}
              <button
                onClick={() => setShowRepurchaseModal(true)}
                className="w-full px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
                style={{ backgroundColor: '#c9a961' }}
              >
                {t('dashboard.convertCashToCredit')}
              </button>
              {user?.role === 'sales-team' && (
                <button
                  onClick={() => setShowRnrConversionModal(true)}
                  className="w-full px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
                  style={{ backgroundColor: '#c9a961' }}
                >
                  {t('dashboard.convertRnrToCredit')}
                </button>
              )}
              <button
                onClick={() => setShowSendPointsModal(true)}
                className="w-full px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
                style={{ backgroundColor: '#8b7355' }}
              >
                {t('dashboard.sendPoints')}
              </button>
            </div>
          </div>

          {/* Referral Card */}
          {referralInfo && (
            <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{t('dashboard.referralCode')}</h2>
              <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#152214', borderColor: '#c9a961' }}>
                <div className="text-body-sm mb-2" style={{ color: '#b8a68f' }}>Code</div>
                <div className="text-headline-md font-bold font-mono" style={{ color: '#c9a961' }}>
                  {referralInfo.affiliate_code}
                </div>
              </div>
              <div className="space-y-2">
                <button
                  onClick={handleCopyReferralLink}
                  className="w-full px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
                  style={{ backgroundColor: '#c9a961' }}
                >
                  {referralCopied ? t('dashboard.copied') : t('dashboard.copyCode')}
                </button>
                <button
                  onClick={() => navigate('/place-referrals')}
                  className="w-full px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
                  style={{ backgroundColor: '#8b7355' }}
                >
                  📍 Place My Referrals
                </button>
              </div>
              <p className="text-body-xs mt-3 text-center" style={{ color: '#b8a68f' }}>
                Share your code to invite others
              </p>
            </div>
          )}
        </div>

        {/* Activity History */}
        <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{t('dashboard.activityHistory')}</h2>
          {transactions.length > 0 ? (
            <div className="space-y-3">
              {transactions.map((txn) => (
                <div key={txn.id} className="flex justify-between items-start p-3 rounded" style={{ backgroundColor: '#152214' }}>
                  <div className="flex-1">
                    <p className="text-body-sm font-medium" style={{ color: '#c9a961' }}>
                      {txn.point_type === 'CREDIT' ? t('dashboard.creditPoints') :
                       txn.point_type === 'CASH' ? t('dashboard.cashPoints') :
                       t('dashboard.productPoints')}
                    </p>
                    {txn.notes && (
                      <p className="text-body-xs mt-1" style={{ color: '#b8a68f' }}>
                        {txn.notes}
                      </p>
                    )}
                    <p className="text-body-xs mt-1" style={{ color: '#999' }}>
                      {formatDateTime(txn.created_at)}
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className={`text-body-sm font-medium ${txn.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {txn.amount >= 0 ? '+' : ''}{txn.amount.toFixed(2)}
                    </p>
                    <p className="text-body-xs mt-1" style={{ color: '#b8a68f' }}>
                      Balance: {txn.balance_after.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-6" style={{ color: '#b8a68f' }}>
              {t('dashboard.noTransactionsYet')}
            </p>
          )}
        </div>

        {/* Direct Referral Commissions - released only, once superadmin approves in /direct-referral */}
        <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>
            {t('dashboard.directReferralCommissions')}
          </h2>
          {referralCommissions.length > 0 ? (
            <div className="space-y-3">
              {referralCommissions.map((c) => {
                const referredName = [c.referred_first_name, c.referred_last_name].filter(Boolean).join(' ');
                return (
                  <div key={c.id} className="flex justify-between items-start p-3 rounded" style={{ backgroundColor: '#152214' }}>
                    <div className="flex-1">
                      <p className="text-body-sm font-medium" style={{ color: '#c9a961' }}>
                        Tier {c.tier} ({c.rate_percent}%)
                      </p>
                      <p className="text-body-xs mt-1" style={{ color: '#b8a68f' }}>
                        {referredName || c.referred_affiliate_code || '—'} ({c.referred_affiliate_code || '—'}) · {c.package_code}
                      </p>
                      <p className="text-body-xs mt-1" style={{ color: '#999' }}>
                        {formatDateTime(c.released_at)}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-body-sm font-medium text-green-400">
                        +{Number(c.amount).toFixed(2)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center py-6" style={{ color: '#b8a68f' }}>
              {t('dashboard.noReferralCommissionsYet')}
            </p>
          )}
        </div>

        {/* Exchange Rates - Members Only */}
        {user?.role !== 'admin' && user?.role !== 'superadmin' && (
          <ExchangeRates />
        )}
          </>
        )}

        {/* Credits & Rates Tab Content - Superadmin Only */}
        {selectedTab === 'credits_rates' && user?.role === 'superadmin' && (
          <div className="space-y-8">

            {/* Top Up Member Credits Section */}
            <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <h3 className="text-headline-sm font-bold mb-6" style={{ color: '#c9a961' }}>
                Top Up Member Credits
              </h3>
              <form onSubmit={handleTopupCredits} className="space-y-4">
                <div>
                  <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>
                    Search Member by Wellis ID
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Wellis ID to search..."
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    className="w-full p-3 rounded border mb-2"
                    style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                  />
                  {memberSearchQuery && (
                    <div className="border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', maxHeight: '200px', overflowY: 'auto' }}>
                      {approvedMembers
                        .filter(member =>
                          member.wellis_id?.toLowerCase().includes(memberSearchQuery.toLowerCase())
                        )
                        .map((member) => (
                          <div
                            key={member.id}
                            onClick={() => {
                              setTopupUserId(member.id);
                              setMemberSearchQuery('');
                            }}
                            className="p-3 border-b cursor-pointer hover:opacity-80 transition-opacity"
                            style={{ borderColor: '#3d5a4a', color: '#b8a68f' }}
                          >
                            <div className="font-medium" style={{ color: '#c9a961' }}>
                              {member.wellis_id}
                            </div>
                            <div className="text-body-xs">
                              {member.first_name} {member.last_name} ({member.email})
                            </div>
                          </div>
                        ))}
                      {memberSearchQuery && approvedMembers.filter(m => m.wellis_id?.toLowerCase().includes(memberSearchQuery.toLowerCase())).length === 0 && (
                        <div className="p-3 text-body-xs" style={{ color: '#999' }}>
                          No members found
                        </div>
                      )}
                    </div>
                  )}
                  {topupUserId && (
                    <div className="p-3 mt-2 rounded border" style={{ backgroundColor: '#152214', borderColor: '#c9a961' }}>
                      <p className="text-body-xs mb-1" style={{ color: '#b8a68f' }}>Selected Member:</p>
                      <p style={{ color: '#c9a961' }}>
                        {approvedMembers.find(m => m.id === topupUserId)?.wellis_id} - {approvedMembers.find(m => m.id === topupUserId)?.first_name} {approvedMembers.find(m => m.id === topupUserId)?.last_name}
                      </p>
                      <button
                        onClick={() => {
                          setTopupUserId('');
                          setMemberSearchQuery('');
                        }}
                        className="text-body-xs mt-2 px-2 py-1 rounded"
                        style={{ backgroundColor: '#3d5a4a', color: '#b8a68f' }}
                      >
                        Clear Selection
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>
                    Amount (SGD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    placeholder="e.g., 100.00"
                    className="w-full p-3 rounded border"
                    style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                  />
                </div>
                <div>
                  <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>
                    Notes <span style={{ color: '#c9a961' }}>*</span> (required)
                  </label>
                  <textarea
                    value={topupNotes}
                    onChange={(e) => setTopupNotes(e.target.value)}
                    placeholder="Bank transfer ref #1023, Stripe charge..."
                    className="w-full p-3 rounded border"
                    style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
                    rows="3"
                  />
                  {topupNotes.trim().length === 0 && (
                    <p className="text-body-xs mt-1" style={{ color: '#c9a961' }}>
                      Notes are required for audit trail
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={processingAction || !topupUserId || !topupAmount || !topupNotes.trim()}
                  className="w-full px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 text-black"
                  style={{ backgroundColor: '#c9a961' }}
                >
                  {processingAction ? 'Processing...' : '+ Add Credits'}
                </button>
              </form>
            </div>

            {/* Exchange Rates Section - Admin View. Reuses the shared, DB-driven
                ExchangeRates component (same one members see) so the superadmin
                console can never drift from the numbers actually charged during
                Credit Points top-up. */}
            <ExchangeRates />
            <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              {/* USDT Processing Fee -- now managed for real at /admin-credit-topups
                  (this used to be a dead stub: /api/admin/usdt-fee/update never
                  existed as a backend route). */}
              <div className="border rounded-lg p-6 mt-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>
                  USDT processing fee and other Credit Points top-up settings have moved to{' '}
                  <a href="/admin-credit-topups" className="underline" style={{ color: '#c9a961' }}>Credit Top-Ups</a>.
                </p>
              </div>

              {/* Credit Repurchase Rate Section */}
              <div className="border rounded-lg p-6 mt-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <h3 className="text-headline-md font-bold mb-4" style={{ color: '#c9a961' }}>Credit Repurchase Rate</h3>
                <p className="text-body-sm mb-4" style={{ color: '#b8a68f' }}>
                  Current Cash Points (CP) to Credit Points (SP) conversion rate
                </p>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4" style={{ backgroundColor: '#152214', borderColor: '#c9a961' }}>
                    <p className="text-body-sm mb-2" style={{ color: '#b8a68f' }}>Current Rate</p>
                    <p className="text-headline-md font-bold" style={{ color: '#c9a961' }}>
                      {repurchaseRate === null ? 'Loading...' : `1 Cash Point = ${repurchaseRate.toFixed(4)} Credit Points`}
                    </p>
                  </div>
                  <div>
                    <label className="block text-body-sm font-medium mb-2" style={{ color: '#b8a68f' }}>
                      New Rate
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min="0.0001"
                      value={repurchaseRateInput}
                      onChange={(e) => setRepurchaseRateInput(e.target.value)}
                      disabled={exchangeRatesLoading || processingAction}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        backgroundColor: '#152214',
                        borderColor: '#c9a961',
                        color: '#c9a961',
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-body-sm font-medium mb-2" style={{ color: '#b8a68f' }}>
                      Notes (optional)
                    </label>
                    <textarea
                      value={repurchaseRateNotes}
                      onChange={(e) => setRepurchaseRateNotes(e.target.value)}
                      disabled={exchangeRatesLoading || processingAction}
                      rows="3"
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 resize-none"
                      style={{
                        backgroundColor: '#152214',
                        borderColor: '#c9a961',
                        color: '#c9a961',
                      }}
                    />
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm(`Change Credit Repurchase Rate from ${repurchaseRate?.toFixed(4) || '1.0000'} to ${repurchaseRateInput || '1.0000'}?`)) {
                        handleUpdateRepurchaseRate();
                      }
                    }}
                    disabled={exchangeRatesLoading || processingAction}
                    className="w-full px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 text-black"
                    style={{ backgroundColor: '#c9a961' }}
                  >
                    Save Rate
                  </button>
                </div>
              </div>

              {/* Exchange Rate Change Audit Trail */}
              <div className="border rounded-lg p-6 mt-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <h3 className="text-headline-md font-bold mb-4" style={{ color: '#c9a961' }}>Exchange Rate Change History</h3>
                {historyLoading ? (
                  <p className="text-body-sm" style={{ color: '#b8a68f' }}>Loading history...</p>
                ) : exchangeRateHistory.length === 0 ? (
                  <p className="text-body-sm" style={{ color: '#b8a68f' }}>No changes yet</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {exchangeRateHistory.map((entry) => (
                      <div key={entry.id} className="border rounded-lg p-3" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="text-body-sm font-medium" style={{ color: '#c9a961' }}>
                              {entry.currency}: {parseFloat(entry.old_rate).toFixed(4)} → {parseFloat(entry.new_rate).toFixed(4)}
                            </p>
                            <p className="text-body-xs" style={{ color: '#b8a68f' }}>
                              Changed by: {entry.admin_wellis_id} ({entry.admin_name})
                            </p>
                          </div>
                          <p className="text-body-xs text-right" style={{ color: '#b8a68f' }}>
                            {formatDateTime(entry.changed_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <PlacementWelcomeModal isOpen={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} />
      <RepurchaseCreditModal
        isOpen={showRepurchaseModal}
        onClose={() => setShowRepurchaseModal(false)}
        onSuccess={() => {
          loadBalances();
          loadTransactions();
        }}
      />
      <RnrConversionModal
        isOpen={showRnrConversionModal}
        onClose={() => setShowRnrConversionModal(false)}
        onSuccess={() => {
          loadBalances();
          loadTransactions();
        }}
      />
      <SendPointsModal
        isOpen={showSendPointsModal}
        onClose={() => setShowSendPointsModal(false)}
        onSuccess={() => {
          loadBalances();
          loadTransactions();
        }}
      />
      <TopUpModal
        isOpen={showTopUpModal}
        onClose={() => setShowTopUpModal(false)}
        onSuccess={() => {
          loadBalances();
          loadTransactions();
        }}
      />
    </div>
  );
}
